/**
 * StartCommunityLiveStreamUseCase
 *
 * Builds and persists the start-live bookkeeping record — the `type = live`
 * community message (carrying its `MessageBroadcastingInfo`) plus the
 * `liveStarted` notification — in one transactional write via
 * `LiveStreamingService.startCommunityLiveStream`. Fired once by the Task-10
 * live-streaming hook when the broadcaster's Agora channel reaches CONNECTED
 * with a RESOLVED local uid.
 *
 * Web analog of Flutter's `community_screen.addLiveMessage`
 * (lib/presentation/screens/community/community_screen.dart:575-639), minus the
 * two presentation-side guards Flutter runs first:
 *   - `firebaseUser == null` -> `false` (`:583-586`)
 *   - `!_internetConnectionActive` -> toast + `false` (`:588-591`)
 * On web those live in the hook/page (which owns toasts + the auth read), NOT in
 * this pure-domain use case.
 *
 * Parity notes on the built entities (`community_screen.dart:593-635`):
 *   - `timestamp = Date.now()` (`:593`), shared by the message, the notification
 *     `info.timestamp`, and the notification `info.broadcastingInfo.timestamp`.
 *   - `owner == userId == broadcasterFirebaseId` (`:600-601`): go-live is only
 *     offered on the creator's OWN community, so the wall owner, the message
 *     author, and the current firebase uid are the same broadcaster identity.
 *   - `broadcasterStreamingId` is the RESOLVED Agora local uid the Task-10 hook
 *     reads off the join — NOT the `0` auto-assign sentinel (story `Top risks`);
 *     the audience matches the broadcaster's remote stream by this value.
 *   - `liveText` is the localized `"{name} is now Live"` string built by the
 *     hook (`:607`); l10n lives in presentation.
 *   - The message entity's OWN `broadcastingInfo` is left UNSET here — the
 *     service attaches the serialized `MessageBroadcastingInfo` onto
 *     `messageJson.broadcastingInfo` (the wire shape is identical either way).
 *   - `notification.id = <uid>_live` via the shared `LIVE_NOTIFICATION_SUFFIX`
 *     (`:634`, `AppConstants.liveNotificationSuffix`).
 *
 * Constructor-injected service (video-call use-case pattern) for testability.
 */

import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import { LIVE_NOTIFICATION_SUFFIX } from '@/domain/constants/notificationConstants';
import {
  LiveStreamingService,
  liveStreamingService,
} from '@/domain/services/liveStreaming/liveStreamingService';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { MessageBroadcastingInfoEntity } from '@/domain/entities/info/messageBroadcastingInfoEntity';

export interface StartCommunityLiveStreamParams {
  /**
   * The broadcaster's Firebase uid — the wall owner, the message author, AND the
   * current firebase uid (all identical on the creator's own community,
   * `community_screen.dart:600-601`). Also forms the `<uid>_live` notification id.
   */
  broadcasterFirebaseId: string;
  /** The broadcaster's display name (message author + notification usersInfo). */
  broadcasterName: string;
  /**
   * The RESOLVED Agora local uid the Task-10 hook reads off the join — never the
   * `0` auto-assign sentinel (story `Top risks`).
   */
  broadcasterStreamingId: number;
  /** The live message `id` (shared by the audience liveness listener). */
  liveStreamingMessageId: string;
  /** Localized `"{name} is now Live"` text built by the hook (l10n in presentation). */
  liveText: string;
}

export class StartCommunityLiveStreamUseCase {
  private service: LiveStreamingService;

  constructor(service: LiveStreamingService = liveStreamingService) {
    this.service = service;
  }

  /**
   * Build the live message + `liveStarted` notification and persist both in one
   * transaction.
   *
   * @returns Promise<boolean> — the service's transactional-write result,
   *   propagated verbatim (parity `community_screen.dart:637-638`).
   */
  async execute(params: StartCommunityLiveStreamParams): Promise<boolean> {
    const {
      broadcasterFirebaseId,
      broadcasterName,
      broadcasterStreamingId,
      liveStreamingMessageId,
      liveText,
    } = params;

    // Shared across message + notification (community_screen.dart:593).
    const timestamp = Date.now();

    // MessageBroadcastingInfo — `broadcasterStreamingId` + `isLive` are set from
    // the join; likes/coins/watchers keep the model defaults 0
    // (community_screen.dart:594-597 + message_broadcasting_info default fields).
    const broadcastingInfo: MessageBroadcastingInfoEntity = {
      broadcasterStreamingId,
      isLive: true,
      likes: 0,
      coins: 0,
      watchers: 0,
    };

    // The live community message. `owner == userId == broadcasterFirebaseId`
    // (community_screen.dart:600-601). The entity's own `broadcastingInfo` is
    // intentionally left unset — the service attaches the serialized
    // MessageBroadcastingInfo (the wire shape is identical either way).
    const message: CommunityChatMessageEntity = {
      id: liveStreamingMessageId,
      text: liveText,
      likes: 0,
      createdTimestamp: timestamp,
      type: CommunityChatMessageType.live,
      success: true,
      messageUsersInfo: {
        owner: broadcasterFirebaseId,
        userId: broadcasterFirebaseId,
        userName: broadcasterName,
      },
    };

    // The `liveStarted` notification (community_screen.dart:613-635). Its id is
    // the deterministic `<uid>_live` (LIVE_NOTIFICATION_SUFFIX).
    const notification: AppNotificationEntity = {
      id: broadcasterFirebaseId + LIVE_NOTIFICATION_SUFFIX,
      info: {
        usersInfo: {
          userId: broadcasterFirebaseId,
          name: broadcasterName,
        },
        broadcastingInfo: {
          liveStreamingMessageId,
          broadcasterStreamingId,
          timestamp,
          isLive: true,
        },
        timestamp,
        notificationType: AppNotificationType.liveStarted,
      },
    };

    return this.service.startCommunityLiveStream({ message, broadcastingInfo, notification });
  }
}

// Export singleton instance
export const startCommunityLiveStreamUseCase = new StartCommunityLiveStreamUseCase();
